import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./ItemImage.module.css";
import { isImageRetained, retainImage } from "./helpers/imageCache";

/**
 * View component, an image of an item either full size or thumbnail.
 * Falls back to a sized placeholder with the item's title when there's no
 * image or it fails to load, since browsers don't reliably constrain a
 * broken <img>'s alt text inside its box.
 */
export function ItemImage({ imageSrc, alt, isThumb }) {
  const imageClass = isThumb ? styles.thumbnail : styles.fullSize;
  const [hasError, setHasError] = useState(false);
  const wasRetained = isImageRetained(imageSrc);

  useEffect(() => {
    setHasError(false);
  }, [imageSrc]);

  return (
    <div className={classNames(styles.itemImage, imageClass)}>
      {!imageSrc || hasError ? (
        <div
          className={styles.placeholder}
          role="img"
          aria-label={alt}
          title={alt}
        >
          {alt}
        </div>
      ) : (
        <img
          className={styles.media}
          src={imageSrc}
          alt={alt}
          loading={wasRetained ? "eager" : "lazy"}
          decoding="async"
          onLoad={() => retainImage(imageSrc)}
          onError={() => setHasError(true)}
        />
      )}
    </div>
  );
}

ItemImage.propTypes = {
  imageSrc: PropTypes.string,
  alt: PropTypes.string.isRequired,
  isThumb: PropTypes.bool,
};
